/**
 * Person Entities - Betrieb Müller
 *
 * All people associated with the farm: employees, contractors, advisors, customers.
 */

import type { PersonEntity } from '@hofsync/design-system';
import { daysAgo, monthsFromNow, yearsAgo } from '../farm-profile';

// ---------------------------------------------------------------------------
// Employees (person_role: employee)
// ---------------------------------------------------------------------------

export const PERSON_KLAUS: PersonEntity = {
  id: 'person-klaus',
  type: 'person',
  name: 'Klaus Müller',
  status: 'active',
  lastUpdated: daysAgo(1),
  properties: {
    person_role: 'employee',
    employment_type: 'permanent',
    role: 'Betriebsinhaber',
    organization: 'Betrieb Müller',
    work_phone: '+49 5131 9087-0',
    work_email: 'klaus@betrieb-mueller.de',
    startDate: yearsAgo(25).toISOString().split('T')[0],
    certifications: ['Sachkundenachweis Pflanzenschutz', 'Sachkundenachweis Tierhaltung'],
    nextTrainingDue: monthsFromNow(4).toISOString().split('T')[0],
  },
  documents: [
    {
      id: 'doc-sachkunde-klaus',
      name: 'Sachkundenachweis Pflanzenschutz - Klaus Müller',
      type: 'zertifikat',
      date: yearsAgo(2.5),
      source: 'Landwirtschaftskammer Niedersachsen',
      status: 'expiring',
      expiresAt: monthsFromNow(4),
      referenceNumber: 'SK-NI-2023-12345',
      extractedData: {
        art: 'Anwender',
        gültigBis: monthsFromNow(4).toISOString().split('T')[0],
        fortbildungErforderlich: true,
      },
      relatedEntityIds: ['person-klaus'],
      satisfiesRequirements: ['pflschsachkv-7'],
    },
    {
      id: 'doc-tierhaltung-klaus',
      name: 'Sachkundenachweis Tierhaltung - Klaus Müller',
      type: 'zertifikat',
      date: yearsAgo(5),
      source: 'Landwirtschaftskammer Niedersachsen',
      status: 'valid',
      referenceNumber: 'TH-NI-2021-56789',
      extractedData: {
        tierart: 'Schwein',
        bestandsobergrenze: 'unbegrenzt',
      },
      relatedEntityIds: ['person-klaus'],
    },
  ],
};

export const PERSON_THOMAS: PersonEntity = {
  id: 'person-thomas',
  type: 'person',
  name: 'Thomas Weber',
  status: 'active',
  lastUpdated: daysAgo(5),
  properties: {
    person_role: 'employee',
    employment_type: 'permanent',
    role: 'Mitarbeiter Außenwirtschaft',
    organization: 'Betrieb Müller',
    work_phone: '+49 5131 9087-12',
    work_email: 'weber@betrieb-mueller.de',
    startDate: yearsAgo(3).toISOString().split('T')[0],
    certifications: ['Sachkundenachweis Pflanzenschutz', 'Führerschein Klasse T'],
    nextTrainingDue: monthsFromNow(8).toISOString().split('T')[0],
  },
  documents: [
    {
      id: 'doc-sachkunde-thomas',
      name: 'Sachkundenachweis Pflanzenschutz - Thomas Weber',
      type: 'zertifikat',
      date: yearsAgo(2),
      source: 'Landwirtschaftskammer Niedersachsen',
      status: 'valid',
      expiresAt: monthsFromNow(8),
      referenceNumber: 'SK-NI-2024-67890',
      extractedData: {
        art: 'Anwender',
        gültigBis: monthsFromNow(8).toISOString().split('T')[0],
        fortbildungErforderlich: false,
      },
      relatedEntityIds: ['person-thomas'],
      satisfiesRequirements: ['pflschsachkv-7'],
    },
    {
      id: 'doc-unterweisung-thomas-2025',
      name: 'Unterweisung Arbeitssicherheit 2025 - Thomas Weber',
      type: 'unterweisung',
      date: daysAgo(20),
      source: 'Betrieb Müller',
      status: 'valid',
      expiresAt: monthsFromNow(11),
      referenceNumber: 'UW-2025-001',
      extractedData: {
        themen: ['Umgang mit Maschinen', 'Gefahrstoffe', 'Erste Hilfe', 'Brandschutz'],
        unterweiser: 'Klaus Müller',
        dauer: '2 Stunden',
      },
      relatedEntityIds: ['person-thomas'],
      satisfiesRequirements: ['arbschg-12'],
    },
  ],
};

// ---------------------------------------------------------------------------
// Contractors (person_role: contractor)
// ---------------------------------------------------------------------------

export const PERSON_KRUEGER: PersonEntity = {
  id: 'person-krueger',
  type: 'person',
  name: 'Stefan Krüger',
  status: 'active',
  lastUpdated: daysAgo(14),
  properties: {
    person_role: 'contractor',
    role: 'Landmaschinenmechaniker',
    organization: 'Landtechnik Schmidt GmbH',
    work_phone: '+49 5131 4422-0',
    work_email: 'krueger@landtechnik-schmidt.de',
  },
  documents: [],
};

// ---------------------------------------------------------------------------
// Advisors (person_role: advisor)
// ---------------------------------------------------------------------------

export const PERSON_SCHULZE: PersonEntity = {
  id: 'person-schulze',
  type: 'person',
  name: 'Dr. Heinrich Schulze',
  status: 'active',
  lastUpdated: daysAgo(30),
  properties: {
    person_role: 'advisor',
    role: 'Pflanzenbauberater',
    organization: 'Landwirtschaftskammer Niedersachsen',
    work_phone: '+49 511 3665-0',
    work_email: 'schulze@lwk-niedersachsen.de',
  },
  documents: [],
};

export const PERSON_BRANDT: PersonEntity = {
  id: 'person-brandt',
  type: 'person',
  name: 'Markus Brandt',
  status: 'active',
  lastUpdated: daysAgo(7),
  properties: {
    person_role: 'advisor',
    role: 'Geschäftsführer Agrar',
    organization: 'Raiffeisen Waren-Zentrale Rhein-Main eG',
    work_phone: '+49 6131 927-300',
    work_email: 'brandt@rwz.de',
  },
  documents: [],
};

// ---------------------------------------------------------------------------
// Customers (person_role: customer)
// ---------------------------------------------------------------------------

export const PERSON_FISCHER: PersonEntity = {
  id: 'person-fischer',
  type: 'person',
  name: 'Anna Fischer',
  status: 'active',
  lastUpdated: daysAgo(45),
  properties: {
    person_role: 'customer',
    role: 'Inhaberin',
    organization: 'Hofladen Fischer',
    work_phone: '+49 5131 7711',
    work_email: 'info@hofladen-fischer.de',
  },
  documents: [],
};

export const PERSON_BERGMANN: PersonEntity = {
  id: 'person-bergmann',
  type: 'person',
  name: 'Thomas Bergmann',
  status: 'active',
  lastUpdated: daysAgo(60),
  properties: {
    person_role: 'customer',
    role: 'Einkaufsleiter',
    organization: 'Edeka Bergmann',
    work_phone: '+49 5131 8899-0',
    work_email: 'bergmann@edeka-bergmann.de',
  },
  documents: [],
};

// ---------------------------------------------------------------------------
// Collections & Helpers
// ---------------------------------------------------------------------------

export const ALL_PERSONS: PersonEntity[] = [
  PERSON_KLAUS,
  PERSON_THOMAS,
  PERSON_KRUEGER,
  PERSON_SCHULZE,
  PERSON_BRANDT,
  PERSON_FISCHER,
  PERSON_BERGMANN,
];

/** @deprecated Use ALL_PERSONS */
export const ALL_EMPLOYEES = ALL_PERSONS;
/** @deprecated Use PERSON_KLAUS */
export const EMPLOYEE_KLAUS = PERSON_KLAUS;
/** @deprecated Use PERSON_THOMAS */
export const EMPLOYEE_THOMAS = PERSON_THOMAS;

export function getPersonById(id: string): PersonEntity | undefined {
  return ALL_PERSONS.find((p) => p.id === id);
}

/** @deprecated Use getPersonById */
export const getEmployeeById = getPersonById;

export function getPersonsWithExpiringCerts(): PersonEntity[] {
  return ALL_PERSONS.filter((p) => {
    const docs = p.documents.filter((d) => d.type === 'zertifikat');
    return docs.some((d) => d.status === 'expiring');
  });
}

/** @deprecated Use getPersonsWithExpiringCerts */
export const getEmployeesWithExpiringCerts = getPersonsWithExpiringCerts;

export function getPersonsNeedingBriefing(): PersonEntity[] {
  const oneYearAgo = yearsAgo(1);
  return ALL_PERSONS.filter((p) => {
    if (p.properties.person_role !== 'employee') return false;
    const briefings = p.documents.filter((d) => d.type === 'unterweisung' && d.date);
    if (briefings.length === 0) return true;
    const latest = briefings.reduce((a, b) =>
      new Date(a.date!) > new Date(b.date!) ? a : b,
    );
    return new Date(latest.date!) < oneYearAgo;
  });
}

/** @deprecated Use getPersonsNeedingBriefing */
export const getEmployeesNeedingBriefing = getPersonsNeedingBriefing;
